import React from "react";
import { getDealsForDashboard } from "../../utils";
import {
  Avatar,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DealCarousel from "./DealCarousel";
import {
  getColorWithMode,
  getBorder,
  HEADING_FONT,
} from "../../constants/colors";
import GearPageHeader from "./GearPageHeader";
import { Info, PlusCircle } from "lucide-react";
import GearExploreList from "../GearExploreList";
import { logUserInteraction } from "../../api/logging";

const gearTypeToPathMap = {
  tent: "tent",
  sleepingPad: "sleeping-pad",
  sleepingBag: "sleeping-bag",
  backpack: "backpacks",
  insulatedJacket: "insulated-jacket",
  stove: "stove",
};

const getProductLinkPath = (gearType, productId) => {
  const typePath = gearTypeToPathMap[gearType];
  return `/gear/${typePath}/${productId}`;
};

const getTextFromgearType = (gearType) => {
  let productType = "products";
  if (gearType === "tent") {
    productType = "tents";
  }
  if (gearType === "sleepingPad") {
    productType = "sleeping pads";
  }
  if (gearType === "sleepingBag") {
    productType = "sleeping bags";
  }
  if (gearType === "backpack") {
    productType = "backpacks";
  }
  if (gearType === "insulatedJacket") {
    productType = "Insulated Jackets";
  }
  if (gearType === "stove") {
    productType = "stoves";
  }

  return productType || "";
};

const GeneralGearContainer = ({ darkMode }) => {
  const [data, setData] = React.useState();

  React.useEffect(() => {
    (async () => {
      logUserInteraction("session", "deals");
      const data = await getDealsForDashboard();
      setData(data);
    })();
  }, []);

  return (
    <Container sx={{ padding: "0px" }}>
      <GearPageHeader
        darkMode={darkMode}
        title="Gear Overview"
        subtitle="Sales, Deals and Tools to help find your next piece of gear"
      />
      <Stack direction="row" justifyContent="center" sx={{ marginTop: "-12px" }}>
        <Button
          href="/gear/submit"
          size="small"
          startIcon={<PlusCircle size={16} />}
          sx={{
            borderRadius: "9px",
            fontFamily: HEADING_FONT,
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          Missing something? Submit gear
        </Button>
      </Stack>
      <Stack
        alignContent="center"
        spacing={3}
        sx={{ width: "100%", padding: "10px" }}
      >
        {data ? (
          <>
            <DealCarousel
              deals={data.allDeals}
              darkMode={darkMode}
              dealDate={data.date}
            />
            <Paper
          elevation={0}
          sx={{
            padding: "14px",
            borderRadius: "16px",
            border: `1px solid ${getBorder(darkMode)}`,
            backgroundImage: "none",
          }}
        >
              <Stack textAlign="center" alignItems="center">
                <Typography
                  variant="h5"
                  sx={{
                    padding: "10px",
                    fontFamily: HEADING_FONT,
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                  }}
                >
                  Most Popular Gear On PackWizard
                </Typography>
                <Grid
                  container
                  justifyContent="center"
                  gap={1}
                  maxWidth="900px"
                >
                  {Object.keys(data.mostPopular).map((mostPopularKey) => (
                    <Paper
                      key={mostPopularKey}
                      elevation={0}
                      sx={{
                        width: "275px",
                        paddingLeft: "5px",
                        paddingRight: "5px",
                        borderRadius: "12px",
                        border: `1px solid ${getBorder(darkMode)}`,
                        backgroundImage: "none",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                        sx={{ paddingLeft: "7px" }}
                      >
                        <Typography
                          sx={{
                            paddingTop: "10px",
                            paddingBottom: "5px",
                            textTransform: "capitalize",
                          }}
                        >
                          {getTextFromgearType(mostPopularKey)}
                        </Typography>
                        <Tooltip
                          title={`These are the most viewed ${getTextFromgearType(
                            mostPopularKey
                          )} on PackWizard`}
                        >
                          <Info
                            size={16}
                            style={{
                              marginLeft: "6px",
                              opacity: 0.6,
                              cursor: "pointer",
                              flexShrink: 0,
                            }}
                          />
                        </Tooltip>
                      </Stack>
                      <Divider />
                      <Stack gap={0.5} sx={{ paddingTop: "5px" }}>
                        {data.mostPopular[mostPopularKey].map((item) => (
                          <Link
                            sx={{ textDecoration: "none" }}
                            href={getProductLinkPath(mostPopularKey, item._id)}
                          >
                            <Card
                              elevation={0}
                              sx={{
                                height: "42px",
                                borderRadius: "8px",
                                backgroundColor: "transparent",
                                backgroundImage: "none",
                                "&:hover": {
                                  backgroundColor: darkMode
                                    ? "rgba(144,202,249,0.08)"
                                    : "rgba(25,118,210,0.05)",
                                  cursor: "pointer",
                                },
                              }}
                            >
                              <Stack direction="row" gap={1}>
                                <Avatar
                                  alt={item.name}
                                  variant="square"
                                  src={item.imageUrl}
                                />
                                <Stack
                                  sx={{ maxWidth: "160px", width: "160px" }}
                                >
                                  <Tooltip
                                    title={`${item.name}${
                                      item.variant ? ` (${item.variant})` : ""
                                    }`}
                                  >
                                    <Typography
                                      textAlign="left"
                                      variant="subtitle2"
                                      sx={{
                                        maxWidth: "200px",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        color: getColorWithMode(
                                          darkMode,
                                          "lightText"
                                        ),
                                      }}
                                    >
                                      {`${item.name}${
                                        item.variant ? ` (${item.variant})` : ""
                                      }`}
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    textAlign="left"
                                    variant="subtitle2"
                                    sx={{
                                      maxWidth: "200px",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      marginTop: "-5px",
                                      color: getColorWithMode(
                                        darkMode,
                                        "lightShadow"
                                      ),
                                    }}
                                  >
                                    {item.formattedBrand}
                                  </Typography>
                                </Stack>
                                <Divider
                                  variant="middle"
                                  orientation="vertical"
                                  flexItem
                                />
                                <Stack
                                  sx={{
                                    width: "40px",
                                    height: "40px",
                                  }}
                                >
                                  <Typography variant="subtitle2">
                                    {`${item.weight.toFixed(0)}g`}
                                  </Typography>
                                  <Divider />
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: getColorWithMode(
                                        darkMode,
                                        "lightShadow"
                                      ),
                                    }}
                                  >
                                    {item.price
                                      ? `$${item.price.toFixed(0)}`
                                      : "-"}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Card>
                          </Link>
                        ))}
                      </Stack>
                    </Paper>
                  ))}
                </Grid>
              </Stack>
            </Paper>
          </>
        ) : (
          <></>
        )}
        <Paper
          elevation={0}
          sx={{
            padding: "14px",
            borderRadius: "16px",
            border: `1px solid ${getBorder(darkMode)}`,
            backgroundImage: "none",
          }}
        >
          <Stack textAlign="center" alignItems="center">
            <Typography
              variant="h5"
              sx={{
                padding: "10px",
                fontFamily: HEADING_FONT,
                fontWeight: 700,
                letterSpacing: "-0.01em",
              }}
            >
              Explore Gear
            </Typography>
            <GearExploreList justifyContent={"center"} />
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
};

export default GeneralGearContainer;
